/**
 * PostIngestionOutlineEditor.jsx
 * 
 * Post-Ingestion Outline Editor - Standalone editing for already-ingested manuscripts.
 * Allows authors and publishers to edit segments after initial ingestion:
 * - Reorder segments (changes audio_order only, not order)
 * - Edit segment titles
 * - Edit segment text (triggers TTS regeneration)
 * - Split segments at cursor position
 * - Delete segments (mark for deletion)
 * 
 * DO NOT import from Gate 3 OutlineEditor components.
 * This is a standalone implementation to avoid coupling.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  GripVertical, Save, Loader2, Check, AlertCircle, X,
  Edit3, ChevronUp, ChevronDown, Trash2, Scissors, Undo2, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

// =============================================================================
// SEGMENT CARD COMPONENT
// =============================================================================
const SegmentCard = ({ 
  segment, 
  index, 
  totalCount,
  onTitleChange, 
  onTextChange,
  onMoveUp, 
  onMoveDown,
  onDelete,
  onRestore,
  onSplit,
  isEditing,
  onEditToggle,
  disabled,
  changeType,
  onSaveSegment,
  saveStatus
}) => {
  const textareaRef = useRef(null);
  const isDeleted = segment.is_deleted;
  const isSaving = saveStatus === 'saving';
  const isSaved = saveStatus === 'success';
  const hasError = saveStatus === 'error';
  
  const handleSplitClick = () => {
    if (textareaRef.current) {
      const cursorPos = textareaRef.current.selectionStart;
      onSplit(segment.segment_id, cursorPos);
    }
  };
  
  const handleSaveClick = () => {
    if (onSaveSegment) {
      onSaveSegment(segment.segment_id);
    }
  };
  
  // Get status badge
  const getStatusBadge = () => {
    if (isDeleted) {
      return <span className="text-xs px-2 py-0.5 bg-red-900/50 text-red-400 rounded">Deleted</span>;
    }
    if (changeType === 'new') {
      return <span className="text-xs px-2 py-0.5 bg-green-900/50 text-green-400 rounded">New</span>;
    }
    if (changeType === 'modified') {
      return <span className="text-xs px-2 py-0.5 bg-yellow-900/50 text-yellow-400 rounded">Modified</span>;
    }
    if (changeType === 'reordered') {
      return <span className="text-xs px-2 py-0.5 bg-blue-900/50 text-blue-400 rounded">Reordered</span>;
    }
    if (changeType === 'title_only') {
      return <span className="text-xs px-2 py-0.5 bg-purple-900/50 text-purple-400 rounded">Renamed</span>;
    }
    return null;
  };
  
  return (
    <div 
      className={`border rounded-lg p-4 transition-all ${
        isDeleted 
          ? 'border-red-800 bg-red-950/30 opacity-60' 
          : 'border-slate-600 bg-slate-800 hover:border-purple-400'
      }`}
      data-testid={`segment-card-${segment.segment_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle + Order */}
        <div className="flex flex-col items-center gap-1 pt-1">
          <div className="cursor-grab text-slate-400 hover:text-white">
            <GripVertical className="w-4 h-4" />
          </div>
          <span className="text-xs font-bold text-purple-400 bg-purple-900/50 px-2 py-0.5 rounded">
            #{index + 1}
          </span>
          <div className="flex flex-col gap-0.5 mt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 text-slate-400 hover:text-white"
              onClick={() => onMoveUp(index)}
              disabled={index === 0 || disabled || isDeleted}
              data-testid={`move-up-${segment.segment_id}`}
            >
              <ChevronUp className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 text-slate-400 hover:text-white"
              onClick={() => onMoveDown(index)}
              disabled={index === totalCount - 1 || disabled || isDeleted}
              data-testid={`move-down-${segment.segment_id}`}
            >
              <ChevronDown className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title Input */}
          <div className="flex items-center gap-2">
            <Input
              value={segment.title || ''}
              onChange={(e) => onTitleChange(segment.segment_id, e.target.value)}
              className={`bg-slate-700 border-slate-600 text-white font-medium text-sm h-8 ${
                isDeleted ? 'line-through opacity-50' : ''
              }`}
              placeholder="Segment title..."
              disabled={disabled || isDeleted}
              data-testid={`segment-title-${segment.segment_id}`}
            />
            {getStatusBadge()}
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <span>{segment.word_count?.toLocaleString() || 0} words</span>
            <span>·</span>
            <span>{segment.char_count?.toLocaleString() || 0} chars</span>
            {segment.duration_ms > 0 && (
              <>
                <span>·</span>
                <span>{Math.round(segment.duration_ms / 1000)}s audio</span>
              </>
            )}
          </div>
          
          {/* Text Editor (collapsible) */}
          {isEditing && !isDeleted && (
            <div className="mt-3 space-y-2">
              <Textarea
                ref={textareaRef}
                value={segment.text || ''}
                onChange={(e) => onTextChange(segment.segment_id, e.target.value)}
                className="bg-slate-700 border-slate-600 text-white min-h-[200px] font-mono text-sm"
                placeholder="Segment text..."
                disabled={disabled}
                data-testid={`segment-text-${segment.segment_id}`}
              />
              <div className="flex items-center justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs border-slate-600 hover:bg-slate-700"
                  onClick={handleSplitClick}
                  disabled={disabled}
                  data-testid={`split-btn-${segment.segment_id}`}
                >
                  <Scissors className="w-3 h-3 mr-1" />
                  Split at cursor
                </Button>
                <span className="text-xs text-slate-500">
                  Position cursor where you want to split, then click
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-1">
          {/* Edit/Save/Close Button */}
          {isEditing ? (
            changeType === 'modified' && !isSaved ? (
              <Button
                size="sm"
                variant="default"
                className={`text-xs ${isSaving ? 'bg-yellow-600 hover:bg-yellow-500' : hasError ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'}`}
                onClick={handleSaveClick}
                disabled={disabled || isDeleted || isSaving}
                data-testid={`save-btn-${segment.segment_id}`}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Generating Audio...
                  </>
                ) : hasError ? (
                  <>
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Retry Save
                  </>
                ) : (
                  <>
                    <Save className="w-3 h-3 mr-1" />
                    Save Changes
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant={isSaved ? "default" : "outline"}
                className={`text-xs ${isSaved ? 'bg-green-600 hover:bg-green-500' : 'bg-purple-600 hover:bg-purple-500'}`}
                onClick={() => onEditToggle(segment.segment_id)}
                disabled={disabled || isDeleted || isSaving}
                data-testid={`close-btn-${segment.segment_id}`}
              >
                {isSaved ? (
                  <>
                    <Check className="w-3 h-3 mr-1" />
                    Audio Updated
                  </>
                ) : (
                  <>
                    <X className="w-3 h-3 mr-1" />
                    Close
                  </>
                )}
              </Button>
            )
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-slate-600"
              onClick={() => onEditToggle(segment.segment_id)}
              disabled={disabled || isDeleted}
              data-testid={`edit-btn-${segment.segment_id}`}
            >
              <Edit3 className="w-3 h-3 mr-1" />
              Edit
            </Button>
          )}
          
          {/* Delete / Restore Button */}
          {isDeleted ? (
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-slate-600 hover:bg-green-900/50 hover:text-green-400"
              onClick={() => onRestore(segment.segment_id)}
              disabled={disabled}
              data-testid={`restore-btn-${segment.segment_id}`}
            >
              <Undo2 className="w-3 h-3 mr-1" />
              Restore
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-slate-600 hover:bg-red-900/50 hover:text-red-400"
              onClick={() => onDelete(segment.segment_id)}
              disabled={disabled}
              data-testid={`delete-btn-${segment.segment_id}`}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// MAIN EDITOR COMPONENT
// =============================================================================
const PostIngestionOutlineEditor = ({ 
  isOpen, 
  onClose, 
  manuscriptId,
  onSaveComplete 
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [segments, setSegments] = useState([]);
  const [originalSegments, setOriginalSegments] = useState([]);
  const [manuscriptData, setManuscriptData] = useState(null);
  const [editingSegmentId, setEditingSegmentId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [changeMap, setChangeMap] = useState({});
  
  // New state for inline segment saves
  const [segmentSaveStatus, setSegmentSaveStatus] = useState({}); // { seg_001: 'saving' | 'success' | 'error' }
  const [segmentsSavedThisSession, setSegmentsSavedThisSession] = useState([]);
  
  // Load data when modal opens
  useEffect(() => {
    if (isOpen && manuscriptId) {
      loadManuscript();
    }
  }, [isOpen, manuscriptId]);
  
  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setSegments([]);
      setOriginalSegments([]);
      setManuscriptData(null);
      setEditingSegmentId(null);
      setHasChanges(false);
      setChangeMap({});
      setLoadError(null);
      setSegmentSaveStatus({});
      setSegmentsSavedThisSession([]);
    }
  }, [isOpen]);
  
  const loadManuscript = async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      console.log('[PostEditEditor] Loading manuscript:', manuscriptId);
      
      const response = await axios.get(
        `${API}/post_edit/load/${manuscriptId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[PostEditEditor] Loaded:', {
        segmentCount: response.data.segments?.length,
        title: response.data.title
      });
      
      setManuscriptData(response.data);
      const loadedSegments = response.data.segments || [];
      setSegments(loadedSegments);
      setOriginalSegments(JSON.parse(JSON.stringify(loadedSegments)));
      setHasChanges(false);
      setChangeMap({});
      setEditingSegmentId(null);
    } catch (error) {
      console.error('[PostEditEditor] Load error:', error);
      const errorMsg = error.response?.data?.detail || 
                       error.response?.data?.message ||
                       'Failed to load manuscript for editing';
      setLoadError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // ==========================================================================
  // CHANGE TRACKING
  // ==========================================================================
  
  const updateChangeMap = useCallback((segmentId, changeType, additionalData = {}) => {
    setChangeMap(prev => ({
      ...prev,
      [segmentId]: {
        ...prev[segmentId],
        type: changeType,
        ...additionalData
      }
    }));
    setHasChanges(true);
  }, []);
  
  const getChangeType = useCallback((segmentId) => {
    return changeMap[segmentId]?.type || null;
  }, [changeMap]);
  
  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  
  const handleTitleChange = useCallback((segmentId, newTitle) => {
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { ...seg, title: newTitle } : seg
    ));
    
    // Check if this is just a title change or if text was also modified
    const existingChange = changeMap[segmentId];
    if (existingChange?.type === 'modified' || existingChange?.type === 'new') {
      // Keep existing change type
    } else {
      updateChangeMap(segmentId, 'title_only');
    }
    setHasChanges(true);
  }, [changeMap, updateChangeMap]);
  
  const handleTextChange = useCallback((segmentId, newText) => {
    console.log('[PostEditEditor] handleTextChange called:', segmentId, 'length:', newText.length);
    
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { 
        ...seg, 
        text: newText,
        word_count: newText.split(/\s+/).filter(w => w).length,
        char_count: newText.length
      } : seg
    ));
    
    // Find original text
    const originalSeg = originalSegments.find(s => s.segment_id === segmentId);
    const originalText = originalSeg?.text || '';
    
    console.log('[PostEditEditor] Text comparison:', {
      segmentId,
      originalLength: originalText.length,
      newLength: newText.length,
      isDifferent: newText !== originalText
    });
    
    // Mark as modified (requires TTS)
    updateChangeMap(segmentId, 'modified', {
      originalText,
      newText,
      text_modified: newText !== originalText
    });
    
    // Explicitly set hasChanges (belt and suspenders - updateChangeMap also sets it)
    setHasChanges(true);
    console.log('[PostEditEditor] hasChanges set to true');
  }, [originalSegments, updateChangeMap]);
  
  const handleMoveUp = useCallback((index) => {
    if (index <= 0) return;
    
    // Capture segment IDs BEFORE state update
    const seg1Id = segments[index - 1]?.segment_id;
    const seg2Id = segments[index]?.segment_id;
    
    // Single setSegments call - swap and update order
    setSegments(prev => {
      const newSegments = [...prev];
      const temp = newSegments[index - 1];
      newSegments[index - 1] = newSegments[index];
      newSegments[index] = temp;
      
      return newSegments.map((seg, idx) => ({ ...seg, audio_order: idx + 1, order: idx + 1 }));
    });
    
    // updateChangeMap called OUTSIDE setSegments
    if (seg1Id && (!changeMap[seg1Id]?.type || changeMap[seg1Id]?.type === 'reordered')) {
      updateChangeMap(seg1Id, 'reordered');
    }
    if (seg2Id && (!changeMap[seg2Id]?.type || changeMap[seg2Id]?.type === 'reordered')) {
      updateChangeMap(seg2Id, 'reordered');
    }
    
    setHasChanges(true);
  }, [segments, changeMap, updateChangeMap]);
  
  const handleMoveDown = useCallback((index) => {
    if (index >= segments.length - 1) return;
    
    // Capture segment IDs BEFORE state update
    const seg1Id = segments[index]?.segment_id;
    const seg2Id = segments[index + 1]?.segment_id;
    
    // Single setSegments call - swap and update order
    setSegments(prev => {
      const newSegments = [...prev];
      const temp = newSegments[index + 1];
      newSegments[index + 1] = newSegments[index];
      newSegments[index] = temp;
      
      return newSegments.map((seg, idx) => ({ ...seg, audio_order: idx + 1, order: idx + 1 }));
    });
    
    // updateChangeMap called OUTSIDE setSegments
    if (seg1Id && (!changeMap[seg1Id]?.type || changeMap[seg1Id]?.type === 'reordered')) {
      updateChangeMap(seg1Id, 'reordered');
    }
    if (seg2Id && (!changeMap[seg2Id]?.type || changeMap[seg2Id]?.type === 'reordered')) {
      updateChangeMap(seg2Id, 'reordered');
    }
    
    setHasChanges(true);
  }, [segments, changeMap, updateChangeMap]);
  
  const handleDelete = useCallback((segmentId) => {
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { ...seg, is_deleted: true } : seg
    ));
    updateChangeMap(segmentId, 'deleted');
    setHasChanges(true);
  }, [updateChangeMap]);
  
  const handleRestore = useCallback((segmentId) => {
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { ...seg, is_deleted: false } : seg
    ));
    
    // Check if it was a new segment or existing
    const originalSeg = originalSegments.find(s => s.segment_id === segmentId);
    if (originalSeg) {
      // It was an existing segment - remove from changeMap or mark as unchanged
      setChangeMap(prev => {
        const next = { ...prev };
        delete next[segmentId];
        return next;
      });
    } else {
      // It was a new segment - keep it as new
      updateChangeMap(segmentId, 'new');
    }
  }, [originalSegments, updateChangeMap]);
  
  const handleSplit = useCallback((segmentId, cursorPos) => {
    const segment = segments.find(s => s.segment_id === segmentId);
    if (!segment || !segment.text) return;
    
    const text = segment.text;
    if (cursorPos <= 0 || cursorPos >= text.length) {
      alert('Please position cursor in the middle of the text to split');
      return;
    }
    
    const text1 = text.slice(0, cursorPos).trim();
    const text2 = text.slice(cursorPos).trim();
    
    if (!text1 || !text2) {
      alert('Both parts must have content after splitting');
      return;
    }
    
    // Generate temp ID for new segment
    const newSegmentId = `new_split_${Date.now()}`;
    
    setSegments(prev => {
      const segmentIndex = prev.findIndex(s => s.segment_id === segmentId);
      if (segmentIndex === -1) return prev;
      
      const newSegments = [...prev];
      
      // Update original segment with first part
      newSegments[segmentIndex] = {
        ...segment,
        text: text1,
        title: `${segment.title} (Part 1)`,
        word_count: text1.split(/\s+/).filter(w => w).length,
        char_count: text1.length
      };
      
      // Create new segment for second part
      const newSegment = {
        segment_id: newSegmentId,
        title: `${segment.title} (Part 2)`,
        order: -1, // Will be assigned by backend
        audio_order: segment.audio_order + 0.5, // Position after original
        text: text2,
        word_count: text2.split(/\s+/).filter(w => w).length,
        char_count: text2.length,
        duration_ms: 0,
        is_new: true,
        is_deleted: false
      };
      
      // Insert after original
      newSegments.splice(segmentIndex + 1, 0, newSegment);
      
      // Renumber audio_order
      return newSegments.map((seg, idx) => ({ ...seg, audio_order: idx + 1 }));
    });
    
    // Mark both as modified (requires TTS)
    updateChangeMap(segmentId, 'modified', { text_modified: true });
    updateChangeMap(newSegmentId, 'new', { text_modified: true });
    setHasChanges(true);
    
    // Close the edit panel for the original segment
    setEditingSegmentId(null);
  }, [segments, updateChangeMap]);
  
  const handleEditToggle = useCallback((segmentId) => {
    setEditingSegmentId(prev => prev === segmentId ? null : segmentId);
  }, []);
  
  // ==========================================================================
  // SAVE
  // ==========================================================================
  
  const handleCancel = () => {
    if (hasChanges && segmentsSavedThisSession.length === 0) {
      if (!confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
    handleClose();
  };
  
  // ==========================================================================
  // SAVE SINGLE SEGMENT (Inline TTS)
  // ==========================================================================
  
  // Poll for segment TTS status (Gate 6 pattern)
  const pollSegmentStatus = useCallback(async (segmentId) => {
    const token = localStorage.getItem('accessToken');
    const pollInterval = 3000; // Poll every 3 seconds
    const maxAttempts = 300; // 15 minutes max (300 * 3s)
    let attempts = 0;
    
    const poll = async () => {
      attempts++;
      
      try {
        const response = await axios.get(
          `${API}/post_edit/segment-status/${manuscriptId}/${segmentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const status = response.data.status;
        console.log(`[PostEditEditor] Poll ${attempts}: segment ${segmentId} status = ${status}`);
        
        if (status === 'completed') {
          // TTS completed successfully
          setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'success' }));
          setSegmentsSavedThisSession(prev => 
            prev.includes(segmentId) ? prev : [...prev, segmentId]
          );
          
          // Clear change tracking for this segment
          setChangeMap(prev => {
            const next = { ...prev };
            delete next[segmentId];
            return next;
          });
          
          // Get current segment for updating originals
          const segment = segments.find(s => s.segment_id === segmentId);
          if (segment) {
            setOriginalSegments(prev => 
              prev.map(s => s.segment_id === segmentId ? { ...segment } : s)
            );
          }
          
          return; // Stop polling
          
        } else if (status === 'failed') {
          // TTS failed
          const errorMsg = response.data.error || 'TTS generation failed';
          setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
          console.error('[PostEditEditor] TTS failed for segment:', segmentId, errorMsg);
          alert(`Audio generation failed for segment: ${errorMsg}`);
          return; // Stop polling
          
        } else if (status === 'in_progress') {
          // Still processing - continue polling
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            // Timeout - mark as error
            setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
            alert('Audio generation timed out. Please try again.');
          }
        } else {
          // Unknown status - continue polling a few more times
          if (attempts < 10) {
            setTimeout(poll, pollInterval);
          } else {
            setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
          }
        }
      } catch (error) {
        console.error('[PostEditEditor] Poll error:', error);
        // Retry a few times on network errors
        if (attempts < 5) {
          setTimeout(poll, pollInterval);
        } else {
          setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
          alert(`Error checking status: ${error.message}`);
        }
      }
    };
    
    // Start polling
    setTimeout(poll, pollInterval);
  }, [manuscriptId, segments]);
  
  const handleSaveSegment = async (segmentId) => {
    const segment = segments.find(s => s.segment_id === segmentId);
    if (!segment) return;
    
    const change = changeMap[segmentId];
    const originalSeg = originalSegments.find(s => s.segment_id === segmentId);
    const textModified = change?.text_modified || (originalSeg && segment.text !== originalSeg.text);
    
    setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'saving' }));
    
    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('[PostEditEditor] Saving segment:', segmentId, { textModified, title: segment.title });
      
      // This call returns immediately - TTS runs in background
      const response = await axios.post(
        `${API}/post_edit/save-segment/${manuscriptId}/${segmentId}`,
        {
          title: segment.title,
          text: segment.text,
          text_modified: textModified,
          order: segment.order,
          r2_text_key: segment.r2_text_key
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000  // 30s timeout for initial request (returns immediately)
        }
      );
      
      console.log('[PostEditEditor] Segment save response:', response.data);
      
      if (response.data.success) {
        if (response.data.background_task) {
          // TTS is running in background - start polling for status
          console.log('[PostEditEditor] Background TTS started, beginning to poll:', response.data.poll_status_endpoint);
          pollSegmentStatus(segmentId);
          // Status remains 'saving' until polling completes
          
        } else {
          // No TTS needed (title-only change) - mark as success immediately
          setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'success' }));
          setSegmentsSavedThisSession(prev => 
            prev.includes(segmentId) ? prev : [...prev, segmentId]
          );
          
          // Clear change tracking
          setChangeMap(prev => {
            const next = { ...prev };
            delete next[segmentId];
            return next;
          });
          
          // Update original segment
          setOriginalSegments(prev => 
            prev.map(s => s.segment_id === segmentId ? { ...segment } : s)
          );
        }
      } else {
        setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
        console.error('[PostEditEditor] Segment save failed:', response.data.error);
        alert(`Failed to save segment: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      setSegmentSaveStatus(prev => ({ ...prev, [segmentId]: 'error' }));
      console.error('[PostEditEditor] Segment save error:', error);
      console.error('[PostEditEditor] Segment error detail:', error.response?.data || error.message || error);
      alert(`Error saving segment: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    }
  };
  
  // ==========================================================================
  // CLOSE EDITOR
  // ==========================================================================
  
  const handleClose = async () => {
    // If any segments were saved this session, deactivate manuscript
    if (segmentsSavedThisSession.length > 0) {
      try {
        const token = localStorage.getItem('accessToken');
        await axios.post(
          `${API}/post_edit/deactivate/${manuscriptId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('[PostEditEditor] Manuscript deactivated');
        
        if (onSaveComplete) {
          onSaveComplete({
            success: true,
            message: 'Manuscript updated. Please Activate to publish changes.',
            deactivated: true
          });
        }
      } catch (e) {
        console.warn('[PostEditEditor] Deactivate failed:', e);
      }
    }
    onClose();
  };
  
  // ==========================================================================
  // SAVE AND CONTINUE - Save all changed segments then close
  // ==========================================================================
  
  const [isSavingAll, setIsSavingAll] = useState(false);
  
  const handleSaveAndContinue = async () => {
    setIsSavingAll(true);
    const token = localStorage.getItem('accessToken');
    
    try {
      // Delete segments first
      const deletedSegments = segments.filter(s => s.is_deleted);
      for (const segment of deletedSegments) {
        await axios.delete(
          `${API}/post_edit/delete-segment/${manuscriptId}/${segment.segment_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Save ALL active segments with current title and order
      segments.forEach(s => console.log('[ORDER DEBUG]', s.segment_id, 'order:', s.order, 'audio_order:', s.audio_order));
      const activeSegments = segments.filter(s => !s.is_deleted);
      for (const segment of activeSegments) {
        await axios.post(
          `${API}/post_edit/save-segment/${manuscriptId}/${segment.segment_id}`,
          {
            title: segment.title,
            text: segment.text || '',
            text_modified: false,
            order: segment.order
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
        );
      }
      
      await axios.post(`${API}/post_edit/deactivate/${manuscriptId}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      
      setHasChanges(false);
      setChangeMap({});
      onClose();
      
    } catch (error) {
      alert(`Failed to save changes: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSavingAll(false);
    }
  };
  
  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  // Count segments requiring TTS
  const ttsRequiredCount = segments.filter(seg => {
    const change = changeMap[seg.segment_id];
    return change?.text_modified && !seg.is_deleted;
  }).length;
  
  // Count active segments (non-deleted)
  const activeSegmentCount = segments.filter(s => !s.is_deleted).length;
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleCancel}>
        <DialogContent 
          className="bg-slate-900 border-slate-700 text-white max-w-5xl max-h-[90vh] flex flex-col"
          data-testid="post-edit-outline-editor"
        >
          <DialogHeader className="flex-shrink-0 border-b border-slate-700 pb-4">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl font-bold">Post-Ingestion Outline Editor</span>
                {manuscriptData && (
                  <span className="text-sm text-slate-400 font-normal">
                    {manuscriptData.title}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span>{activeSegmentCount} segments</span>
                {ttsRequiredCount > 0 && (
                  <span className="text-yellow-400">
                    {ttsRequiredCount} will regenerate audio
                  </span>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {/* Content Area */}
          <div className="flex-1 overflow-y-auto py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                <span className="ml-3 text-slate-400">Loading manuscript...</span>
              </div>
            ) : loadError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                <p className="text-red-400 font-medium mb-2">Unable to Load</p>
                <p className="text-slate-400 text-sm max-w-md">{loadError}</p>
                <Button 
                  onClick={loadManuscript} 
                  className="mt-4"
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="space-y-3 px-1">
                {segments.map((segment, index) => (
                  <SegmentCard
                    key={segment.segment_id}
                    segment={segment}
                    index={index}
                    totalCount={segments.length}
                    onTitleChange={handleTitleChange}
                    onTextChange={handleTextChange}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    onSplit={handleSplit}
                    isEditing={editingSegmentId === segment.segment_id}
                    onEditToggle={handleEditToggle}
                    disabled={segmentSaveStatus[segment.segment_id] === 'saving'}
                    changeType={changeMap[segment.segment_id]?.type || null}
                    onSaveSegment={handleSaveSegment}
                    saveStatus={segmentSaveStatus[segment.segment_id]}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Footer - Save and Continue + Close buttons */}
          <DialogFooter className="flex-shrink-0 border-t border-slate-700 pt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-slate-400">
                {segmentsSavedThisSession.length > 0 ? (
                  <span className="flex items-center gap-2 text-green-400">
                    <Check className="w-4 h-4" />
                    {segmentsSavedThisSession.length} segment{segmentsSavedThisSession.length > 1 ? 's' : ''} saved
                  </span>
                ) : hasChanges ? (
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    Unsaved changes
                  </span>
                ) : (
                  <span>No changes</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSaveAndContinue}
                  disabled={!hasChanges || isSavingAll}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="save-and-continue-btn"
                >
                  {isSavingAll ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save and Continue
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="border-slate-600"
                  data-testid="close-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostIngestionOutlineEditor;
